// Admin · Módulo de Facturación
// Vista global de todas las CFE emitidas (B2C suscripción + B2C one-shot + B2B).
// Permite: ver detalle, emitir CFE pendiente, emitir Nota de Crédito,
// bajar XML/PDF firmados, exportar CSV.
//
// Solo admin. Cuelga de /admin/facturacion (sidebar Administración).

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

/* ─────────────────────────────────────────────────────────────────────────────
 * Convenciones de tipo_cfe — soportamos ambas convenciones:
 *   - Legacy (cargado a mano por Roberto desde EmpresaDetalle): e_factura, e_ticket, etc.
 *   - Biller (emitidas por edge function emitir-cfe / emitir-nota-credito):
 *     eTicket, eFactura, eTicketNotaCredito, eFacturaNotaCredito, etc.
 * ─────────────────────────────────────────────────────────────────────────── */

// 17-jul: la columna receptor_rut guarda cualquier documento (RUT o CI). Diferenciamos
// por largo: 12 dígitos → RUT (uruguayo, 6 emisor + 6 correlativo), 7-8 dígitos → CI.
function tipoDocLabel(doc) {
  const d = String(doc || '').replace(/\D/g, '')
  if (d.length === 12) return 'RUT'
  if (d.length >= 7 && d.length <= 8) return 'CI'
  return 'Doc'
}

const TIPO_CFE_LABEL = {
  // Biller (camelCase) — preferidos
  eTicket: '🎫 e-Ticket',
  eFactura: '📄 e-Factura',
  eTicketNotaCredito: '↩ NC e-Ticket',
  eFacturaNotaCredito: '↩ NC e-Factura',
  eTicketNotaDebito: '➕ ND e-Ticket',
  eFacturaNotaDebito: '➕ ND e-Factura',
  // Legacy (snake_case)
  e_factura: '📄 e-Factura',
  e_ticket: '🎫 e-Ticket',
  e_nota_credito: '↩ Nota de Crédito',
  e_nota_debito: '➕ Nota de Débito',
  e_resguardo: '🛡 Resguardo',
  otro: '📑 Otro',
}

function esNotaCredito(tipo) {
  return tipo === 'eTicketNotaCredito' || tipo === 'eFacturaNotaCredito' || tipo === 'e_nota_credito'
}

const ESTADO_COLOR = {
  emitida: { bg: '#DCFCE7', fg: '#16A34A', label: 'Emitida' },
  aprobada: { bg: '#DCFCE7', fg: '#16A34A', label: 'Aprobada DGI' },
  pendiente: { bg: '#FEF3C7', fg: '#92400E', label: 'Pendiente' },
  rechazada: { bg: '#FEE2E2', fg: '#DC2626', label: 'Rechazada DGI' },
  anulada: { bg: '#FEE2E2', fg: '#DC2626', label: 'Anulada' },
}

// 5 estados oficiales DGI según Biller (mail Alejandro Mercadal 2026-06-01).
// Aplica sólo a facturas con sync_origen='biller'. Las manuales no van a DGI desde acá.
const ESTADO_DGI_COLOR = {
  'Pendiente DGI':         { bg: '#FEF3C7', fg: '#92400E', label: '⏳ Pendiente DGI',  short: 'Pendiente' },
  'Envío no corresponde':  { bg: '#E2E8F0', fg: '#475569', label: '— No corresponde',   short: 'No corresp.' },
  'Aceptado DGI':          { bg: '#DCFCE7', fg: '#16A34A', label: '✓ Aceptado DGI',     short: 'Aceptado' },
  'Rechazado DGI':         { bg: '#FEE2E2', fg: '#DC2626', label: '✗ Rechazado DGI',    short: 'Rechazado' },
  // Observado en naranja para diferenciar de Rechazado (antes ambos eran rojo).
  // Observado = DGI lo aceptó pero pide aclaración/correción; Rechazado = lo niega.
  'Observado DGI':         { bg: '#FFEDD5', fg: '#C2410C', label: '⚠ Observado DGI',    short: 'Observado' },
}

const SYNC_ORIGEN_LABEL = {
  biller: '🔗 Biller',
  zsoftware: '📦 Zsoftware',
  manual: '✍ Manual',
}

function fmtFecha(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtMoneda(n, moneda = 'UYU') {
  const num = Number(n || 0)
  return `${moneda === 'USD' ? 'US$' : '$'}${num.toLocaleString('es-UY', { maximumFractionDigits: 2 })}`
}

function inicioDeMes() {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Componente principal
 * ─────────────────────────────────────────────────────────────────────────── */

// Canal de venta derivado de los IDs de la factura (sin joins).
// Prioridad: ML > B2B (empresa) > Suscripción (cliente suscriptor) > One Shot (pedido).
function canalDeFactura(f) {
  if (f.ml_venta_id) return 'ML'
  if (f.empresa_id || f.suscripcion_empresa_id) return 'B2B'
  if (f.cliente_id) return 'Suscripción'
  if (f.pedido_id) return 'One Shot'
  return '—'
}
const CANAL_STYLE = {
  'ML':          { bg: '#FEF3C7', fg: '#92400E' },
  'B2B':         { bg: '#DBEAFE', fg: '#1E40AF' },
  'Suscripción': { bg: '#D1FAE5', fg: '#065F46' },
  'One Shot':    { bg: '#EDE9FE', fg: '#5B21B6' },
  '—':           { bg: '#F1F5F9', fg: '#64748B' },
}

export default function Facturacion() {
  const [facturas, setFacturas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)

  // Filtros
  const [busqueda, setBusqueda] = useState('')
  const [estado, setEstado] = useState('todos')
  const [estadoDgi, setEstadoDgi] = useState('todos')
  const [pollingLoading, setPollingLoading] = useState(false)
  const [pollingResultado, setPollingResultado] = useState(null)
  const [tipo, setTipo] = useState('todos')
  const [origen, setOrigen] = useState('todos')
  const [desde, setDesde] = useState('')  // default: ver TODO (antes arrancaba el 1º del mes y escondía el histórico)
  const [hasta, setHasta] = useState('')
  // Toggle "mostrar facturas marcadas como prueba" - default OFF para que Roberto
  // no las vea por defecto. Persistido en localStorage para que quede el estado.
  const [mostrarPruebas, setMostrarPruebas] = useState(() => {
    try { return localStorage.getItem('limpack_facturacion_ver_pruebas') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('limpack_facturacion_ver_pruebas', mostrarPruebas ? '1' : '0') } catch {}
  }, [mostrarPruebas])

  // Estado UI
  const [expandida, setExpandida] = useState(null)
  const [modalNC, setModalNC] = useState(null)
  const [accionando, setAccionando] = useState(null)
  const [mensaje, setMensaje] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setCargando(true)
    setError(null)
    try {
      const { data, error: e } = await supabase
        .from('facturas_emitidas')
        .select(`
          id, tipo_cfe, serie, numero, fecha_emision,
          receptor_razon_social, receptor_rut,
          subtotal, iva_total, monto_total, moneda,
          estado, estado_dgi, sync_origen,
          xml_path, pdf_path,
          biller_cfe_id, biller_sync_at,
          biller_payload, biller_response,
          factura_original_id, nota_credito_motivo, nota_credito_monto,
          cliente_id, empresa_id, pedido_id, suscripcion_empresa_id, ml_venta_id,
          anulada_at, anulada_motivo,
          es_prueba,
          created_at
        `)
        .order('fecha_emision', { ascending: false })
        .limit(500)
      if (e) throw e
      setFacturas(data || [])
    } catch (e) {
      setError(e.message || String(e))
    }
    setCargando(false)
  }

  /* ── Filtrado en memoria ───────────────────────────────────────────────── */

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return facturas.filter(f => {
      if (!mostrarPruebas && f.es_prueba) return false
      if (estado !== 'todos' && f.estado !== estado) return false
      if (estadoDgi !== 'todos' && (f.estado_dgi || '—') !== estadoDgi) return false
      if (tipo !== 'todos' && f.tipo_cfe !== tipo) return false
      if (origen !== 'todos' && (f.sync_origen || 'manual') !== origen) return false
      if (desde && f.fecha_emision && f.fecha_emision < desde) return false
      if (hasta && f.fecha_emision && f.fecha_emision > hasta) return false
      if (q) {
        const hay = [
          f.serie, f.numero, f.receptor_razon_social, f.receptor_rut,
          f.biller_cfe_id, f.nota_credito_motivo,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [facturas, busqueda, estado, estadoDgi, tipo, origen, desde, hasta, mostrarPruebas])

  const totalPruebas = useMemo(() => facturas.filter(f => f.es_prueba).length, [facturas])

  /* ── KPIs ──────────────────────────────────────────────────────────────── */

  const kpis = useMemo(() => {
    // KPIs SIEMPRE excluyen facturas es_prueba (números reales de negocio)
    const facturasReales = facturas.filter(f => !f.es_prueba)
    const mesIni = inicioDeMes()
    const delMes = facturasReales.filter(f => f.fecha_emision && f.fecha_emision >= mesIni)
    const noNC = delMes.filter(f => !esNotaCredito(f.tipo_cfe))
    const totalMes = noNC.reduce((s, f) => s + Number(f.monto_total || 0), 0)
    // Breakdown B2B (con empresa_id) vs B2C (todo lo demás: pedidos one-shot personales, suscripciones, ML)
    const noNC_B2B = noNC.filter(f => !!f.empresa_id)
    const noNC_B2C = noNC.filter(f => !f.empresa_id)
    const totalB2B = noNC_B2B.reduce((s, f) => s + Number(f.monto_total || 0), 0)
    const totalB2C = noNC_B2C.reduce((s, f) => s + Number(f.monto_total || 0), 0)
    const ncMes = delMes.filter(f => esNotaCredito(f.tipo_cfe))
    const totalNC = ncMes.reduce((s, f) => s + Number(f.monto_total || 0), 0)
    const aprobadasDGI = delMes.filter(f => f.estado === 'aprobada' || f.estado === 'emitida').length
    const totalCFE = delMes.length
    const pctAprobadas = totalCFE > 0 ? Math.round((aprobadasDGI / totalCFE) * 100) : 0
    const pendientes = facturasReales.filter(f => f.estado === 'pendiente').length
    const pendientesDGI = facturasReales.filter(f => f.estado_dgi === 'Pendiente DGI').length
    const rechazadasDGI = facturasReales.filter(f => f.estado_dgi === 'Rechazado DGI' || f.estado_dgi === 'Observado DGI').length
    return {
      totalMes,
      totalB2B,
      totalB2C,
      cantB2B: noNC_B2B.length,
      cantB2C: noNC_B2C.length,
      neto: totalMes - totalNC,
      cantidadMes: totalCFE,
      pctAprobadas,
      ncCount: ncMes.length,
      ncMonto: totalNC,
      pendientes,
      pendientesDGI,
      rechazadasDGI,
    }
  }, [facturas])

  /* ── Acciones ──────────────────────────────────────────────────────────── */

  async function descargarArchivo(f, kind) {
    const path = kind === 'pdf' ? f.pdf_path : f.xml_path
    if (!path) return
    setAccionando(`${f.id}-${kind}`)
    try {
      const { data, error: e } = await supabase.storage
        .from('facturas-emitidas')
        .createSignedUrl(path, 300)
      if (e) throw e
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (e) {
      alert('No se pudo descargar: ' + e.message)
    }
    setAccionando(null)
  }

  async function sincronizarEstadosDGI() {
    setPollingLoading(true)
    setPollingResultado(null)
    try {
      const { data, error: e } = await supabase.functions.invoke('actualizar-estado-cfe', { body: { limit: 100 } })
      if (e) throw e
      if (data?.ok === false || data?.error) {
        setPollingResultado({ tipo: 'error', texto: `Error sincronizando DGI: ${data?.error || 'desconocido'}` })
      } else {
        const r = data?.resumen || {}
        if (r.procesadas === 0) {
          setPollingResultado({ tipo: 'ok', texto: 'No hay facturas en Pendiente DGI.' })
        } else {
          const detalle = Object.entries(r.estados || {}).map(([k, v]) => `${v} → ${k}`).join(' · ')
          setPollingResultado({
            tipo: r.con_error > 0 ? 'warning' : 'ok',
            texto: `Procesadas ${r.procesadas}: ${r.actualizadas} actualizadas, ${r.sin_cambio} sin cambio${r.con_error ? `, ${r.con_error} con error` : ''}${detalle ? ` (${detalle})` : ''}`,
          })
        }
        cargar()
      }
    } catch (e) {
      setPollingResultado({ tipo: 'error', texto: 'Error: ' + (e.message || String(e)) })
    }
    setPollingLoading(false)
  }

  async function emitirCFE(f) {
    if (!f.pedido_id) {
      alert('Esta factura no tiene pedido_id asociado, no se puede emitir desde la UI. (Las facturas de suscripción se emiten automáticamente al confirmar el cobro.)')
      return
    }
    if (!confirm('¿Disparar emitir-cfe para este pedido?')) return
    setAccionando(`${f.id}-emitir`)
    try {
      const payload = { pedido_id: f.pedido_id }
      const { data, error: e } = await supabase.functions.invoke('emitir-cfe', { body: payload })
      if (e) throw e
      if (data?.ok === false || data?.error) {
        const msg = data?.message || data?.error || 'Error desconocido'
        if (data?.error === 'BILLER_NOT_CONFIGURED') {
          setMensaje({ tipo: 'warning', texto: '⚠ Biller no está configurado todavía. La función responde 501 hasta que llegue el token.' })
        } else {
          setMensaje({ tipo: 'error', texto: `Error: ${msg}` })
        }
      } else {
        setMensaje({ tipo: 'ok', texto: `✓ CFE emitido. Biller ID: ${data?.biller_cfe_id || '—'}` })
        cargar()
      }
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + (e.message || String(e)) })
    }
    setAccionando(null)
  }

  async function emitirNC({ motivo, monto, anularTotal }) {
    const original = modalNC
    if (!original || !motivo?.trim()) return
    setAccionando(`${original.id}-nc`)
    try {
      const body = {
        factura_original_id: original.id,
        motivo: motivo.trim(),
      }
      if (!anularTotal && monto && Number(monto) > 0) {
        body.monto = Number(monto)
      }
      const { data, error: e } = await supabase.functions.invoke('emitir-nota-credito', { body })
      if (e) throw e
      if (data?.ok === false || data?.error) {
        const msg = data?.message || data?.error || 'Error desconocido'
        if (data?.error === 'BILLER_NOT_CONFIGURED') {
          setMensaje({ tipo: 'warning', texto: '⚠ Biller no está configurado todavía. La función responde 501 hasta que llegue el token.' })
        } else {
          setMensaje({ tipo: 'error', texto: `Error: ${msg}` })
        }
      } else if (data?.already_issued) {
        setMensaje({ tipo: 'warning', texto: `Ya existía una NC para esta factura (${data.numero})` })
        cargar()
      } else {
        setMensaje({ tipo: 'ok', texto: `✓ NC emitida ${data?.serie || ''}-${data?.numero || ''} · ${data?.anulacion || 'parcial'}` })
        cargar()
      }
      setModalNC(null)
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + (e.message || String(e)) })
    }
    setAccionando(null)
  }

  function exportarCSV() {
    if (filtradas.length === 0) return
    const headers = [
      'fecha', 'tipo_cfe', 'canal', 'serie', 'numero', 'receptor_rut', 'receptor_razon_social',
      'subtotal', 'iva_total', 'monto_total', 'moneda', 'estado', 'estado_dgi', 'sync_origen',
      'biller_cfe_id', 'factura_original_id', 'nota_credito_motivo',
    ]
    const rows = filtradas.map(f => [
      f.fecha_emision || '',
      f.tipo_cfe || '',
      canalDeFactura(f),
      f.serie || '',
      f.numero || '',
      f.receptor_rut || '',
      (f.receptor_razon_social || '').replace(/[,\n\r"]/g, ' '),
      f.subtotal || 0,
      f.iva_total || 0,
      f.monto_total || 0,
      f.moneda || 'UYU',
      f.estado || '',
      f.estado_dgi || '',
      f.sync_origen || '',
      f.biller_cfe_id || '',
      f.factura_original_id || '',
      (f.nota_credito_motivo || '').replace(/[,\n\r"]/g, ' '),
    ])
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `facturacion_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /* ── Render ────────────────────────────────────────────────────────────── */

  const tiposPresentes = useMemo(() => {
    const s = new Set(facturas.map(f => f.tipo_cfe).filter(Boolean))
    return Array.from(s)
  }, [facturas])

  return (
    <AdminLayout title="Facturación">
      <div className="fc">
        <header className="fc-header">
          <div>
            <h1>Facturación</h1>
            <p className="fc-sub">CFE emitidos · todas las fuentes (B2C, B2B, suscripciones, one-shot)</p>
          </div>
          <button className="fc-btn-secondary" onClick={cargar} disabled={cargando}>
            {cargando ? 'Cargando…' : '↻ Refrescar'}
          </button>
        </header>

        {/* ── KPIs ──────────────────────────────────────────────────────── */}
        <section className="fc-kpis">
          <KpiCard
            label="Facturado este mes"
            value={fmtMoneda(kpis.totalMes)}
            sub={
              kpis.cantidadMes === 0
                ? '0 comprobantes'
                : `${kpis.cantidadMes} comprobantes · B2B ${fmtMoneda(kpis.totalB2B)} · B2C ${fmtMoneda(kpis.totalB2C)}`
            }
            color="navy"
          />
          <KpiCard label="% aceptado DGI" value={`${kpis.pctAprobadas}%`} sub="del mes en curso" color="teal" />
          <KpiCard label="Notas de crédito" value={String(kpis.ncCount)} sub={fmtMoneda(kpis.ncMonto)} color="warn" />
          <KpiCard label="Pendientes DGI" value={String(kpis.pendientesDGI)} sub={kpis.rechazadasDGI > 0 ? `⚠ ${kpis.rechazadasDGI} rechazada(s)` : 'Polling cada 20 min'} color={kpis.pendientesDGI > 0 ? 'warn' : 'ok'} />
          <KpiCard label="Neto del mes" value={fmtMoneda(kpis.neto)} sub={kpis.pendientes > 0 ? `⚠ ${kpis.pendientes} pendiente(s)` : 'Sin pendientes'} color={kpis.pendientes > 0 ? 'warn' : 'ok'} />
        </section>

        {mensaje && (
          <div className={`fc-msg fc-msg-${mensaje.tipo}`}>
            {mensaje.texto}
            <button className="fc-msg-close" onClick={() => setMensaje(null)}>✕</button>
          </div>
        )}

        {/* ── Filtros ───────────────────────────────────────────────────── */}
        <section className="fc-filtros">
          <input
            type="search"
            placeholder="Buscar por serie, número, RUT, razón social, Biller ID…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="fc-input fc-input-search"
          />
          <select value={estado} onChange={e => setEstado(e.target.value)} className="fc-select">
            <option value="todos">Estado: todos</option>
            <option value="aprobada">Aprobada DGI</option>
            <option value="emitida">Emitida</option>
            <option value="pendiente">Pendiente</option>
            <option value="rechazada">Rechazada DGI</option>
            <option value="anulada">Anulada</option>
          </select>
          <select value={estadoDgi} onChange={e => setEstadoDgi(e.target.value)} className="fc-select">
            <option value="todos">DGI: todos</option>
            <option value="Pendiente DGI">⏳ Pendiente DGI</option>
            <option value="Aceptado DGI">✓ Aceptado DGI</option>
            <option value="Rechazado DGI">✗ Rechazado DGI</option>
            <option value="Observado DGI">⚠ Observado DGI</option>
            <option value="Envío no corresponde">— No corresponde</option>
            <option value="—">Sin enviar (manual)</option>
          </select>
          <select value={tipo} onChange={e => setTipo(e.target.value)} className="fc-select">
            <option value="todos">Tipo: todos</option>
            {tiposPresentes.map(t => (
              <option key={t} value={t}>{TIPO_CFE_LABEL[t] || t}</option>
            ))}
          </select>
          <select value={origen} onChange={e => setOrigen(e.target.value)} className="fc-select">
            <option value="todos">Origen: todos</option>
            <option value="biller">Biller</option>
            <option value="zsoftware">Zsoftware</option>
            <option value="manual">Manual</option>
          </select>
          <label className="fc-date">
            <span>Desde</span>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
          </label>
          <label className="fc-date">
            <span>Hasta</span>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
          </label>
          <button
            className="fc-btn-sync"
            onClick={sincronizarEstadosDGI}
            disabled={pollingLoading}
            title="Consulta Biller para actualizar estado DGI de facturas en 'Pendiente DGI'"
          >
            {pollingLoading ? '…' : '🔄'} Sincronizar DGI
          </button>
          <button className="fc-btn-export" onClick={exportarCSV} disabled={filtradas.length === 0}>
            ⬇ CSV
          </button>
          {totalPruebas > 0 && (
            <button
              type="button"
              onClick={() => setMostrarPruebas(v => !v)}
              title={mostrarPruebas ? 'Ocultar facturas marcadas como prueba' : 'Mostrar facturas marcadas como prueba (test/desarrollo)'}
              style={{
                padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: mostrarPruebas ? '#FEF3C7' : '#F1F5F9',
                color: mostrarPruebas ? '#78350F' : '#475569',
                border: `1px solid ${mostrarPruebas ? '#FCD34D' : '#CBD5E1'}`,
              }}
            >
              🧪 {mostrarPruebas ? `Ocultar pruebas (${totalPruebas})` : `Ver pruebas (${totalPruebas})`}
            </button>
          )}
        </section>

        {pollingResultado && (
          <div className={`fc-msg fc-msg-${pollingResultado.tipo}`}>
            {pollingResultado.texto}
            <button className="fc-msg-close" onClick={() => setPollingResultado(null)}>✕</button>
          </div>
        )}

        {/* ── Tabla ─────────────────────────────────────────────────────── */}
        {error && <div className="fc-error">⚠ {error}</div>}

        {cargando ? (
          <div className="fc-loading">Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div className="fc-empty">
            <p>Sin facturas que coincidan con los filtros.</p>
            {facturas.length === 0 && (
              <p className="fc-empty-sub">
                Cuando se confirme el primer cobro y Biller esté activo, los CFE aparecen acá automáticamente.
              </p>
            )}
          </div>
        ) : (
          <div className="fc-tabla-wrap">
            <table className="fc-tabla">
              <thead>
                <tr>
                  <th></th>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Canal</th>
                  <th>Nº</th>
                  <th>Receptor</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>DGI</th>
                  <th>Origen</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map(f => (
                  <FilaFactura
                    key={f.id}
                    f={f}
                    expandida={expandida === f.id}
                    onToggle={() => setExpandida(expandida === f.id ? null : f.id)}
                    onDescargar={descargarArchivo}
                    onEmitirCFE={emitirCFE}
                    onAbrirNC={() => setModalNC(f)}
                    accionando={accionando}
                    facturas={facturas}
                  />
                ))}
              </tbody>
            </table>
            <div className="fc-footer-tabla">
              {filtradas.length} de {facturas.length} facturas
            </div>
          </div>
        )}

        {/* ── Modal NC ──────────────────────────────────────────────────── */}
        {modalNC && (
          <ModalEmitirNC
            factura={modalNC}
            onCerrar={() => setModalNC(null)}
            onEmitir={emitirNC}
            accionando={accionando === `${modalNC.id}-nc`}
          />
        )}
      </div>
      <style>{css}</style>
    </AdminLayout>
  )
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Subcomponentes
 * ─────────────────────────────────────────────────────────────────────────── */

function KpiCard({ label, value, sub, color = 'navy' }) {
  return (
    <div className={`fc-kpi fc-kpi-${color}`}>
      <div className="fc-kpi-label">{label}</div>
      <div className="fc-kpi-value">{value}</div>
      <div className="fc-kpi-sub">{sub}</div>
    </div>
  )
}

function FilaFactura({ f, expandida, onToggle, onDescargar, onEmitirCFE, onAbrirNC, accionando, facturas }) {
  const est = ESTADO_COLOR[f.estado] || { bg: '#E2E8F0', fg: '#475569', label: f.estado || '—' }
  const esNC = esNotaCredito(f.tipo_cfe)
  const tieneNC = f.factura_original_id
    ? false
    : facturas.some(x => x.factura_original_id === f.id)
  const puedeEmitirCFE = !f.biller_cfe_id && f.estado !== 'anulada' && f.pedido_id
  const puedeEmitirNC = f.biller_cfe_id && !esNC && !tieneNC && f.estado !== 'anulada'

  return (
    <>
      <tr className={`fc-fila ${f.estado === 'anulada' ? 'fc-fila-anulada' : ''}`} style={f.es_prueba ? { background: 'rgba(254,243,199,.35)' } : undefined}>
        <td>
          <button className="fc-btn-toggle" onClick={onToggle} title="Ver detalle">
            {expandida ? '▼' : '▶'}
          </button>
        </td>
        <td>{fmtFecha(f.fecha_emision)}</td>
        <td>
          {TIPO_CFE_LABEL[f.tipo_cfe] || f.tipo_cfe}
          {f.es_prueba && (
            <div style={{ display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 9.5, fontWeight: 800, background: '#FEF3C7', color: '#78350F', border: '1px solid #FCD34D', letterSpacing: '.5px' }} title="Factura marcada como prueba - no borrar por regla fiscal">
              PRUEBA
            </div>
          )}
        </td>
        <td>
          {(() => { const cn = canalDeFactura(f); const cs = CANAL_STYLE[cn] || CANAL_STYLE['—']; return <span className="fc-badge" style={{ background: cs.bg, color: cs.fg }}>{cn}</span> })()}
        </td>
        <td><strong>{f.serie ? `${f.serie}-` : ''}{f.numero || '—'}</strong></td>
        <td className="fc-receptor">
          {f.receptor_razon_social || <span className="fc-muted">Consumidor final</span>}
          {f.receptor_rut && <div className="fc-mini">{tipoDocLabel(f.receptor_rut)} {f.receptor_rut}</div>}
        </td>
        <td className="fc-num"><strong>{fmtMoneda(f.monto_total, f.moneda)}</strong></td>
        <td>
          <span className="fc-badge" style={{ background: est.bg, color: est.fg }}>{est.label}</span>
          {tieneNC && <div className="fc-mini fc-mini-warn">↩ con NC</div>}
        </td>
        <td>
          {f.estado_dgi ? (
            <span
              className="fc-badge"
              style={{ background: (ESTADO_DGI_COLOR[f.estado_dgi] || {}).bg || '#E2E8F0', color: (ESTADO_DGI_COLOR[f.estado_dgi] || {}).fg || '#475569' }}
              title={f.estado_dgi}
            >
              {(ESTADO_DGI_COLOR[f.estado_dgi] || {}).short || f.estado_dgi}
            </span>
          ) : (
            <span className="fc-muted">—</span>
          )}
        </td>
        <td>{SYNC_ORIGEN_LABEL[f.sync_origen] || SYNC_ORIGEN_LABEL.manual}</td>
        <td>
          <div className="fc-acciones">
            {f.pdf_path && (
              <button
                className="fc-btn-link"
                disabled={accionando === `${f.id}-pdf`}
                onClick={() => onDescargar(f, 'pdf')}
                title="Bajar PDF"
              >
                {accionando === `${f.id}-pdf` ? '…' : '📄'}
              </button>
            )}
            {f.xml_path && (
              <button
                className="fc-btn-link"
                disabled={accionando === `${f.id}-xml`}
                onClick={() => onDescargar(f, 'xml')}
                title="Bajar XML"
              >
                {accionando === `${f.id}-xml` ? '…' : '📋'}
              </button>
            )}
            {puedeEmitirCFE && (
              <button
                className="fc-btn-primary-mini"
                disabled={accionando === `${f.id}-emitir`}
                onClick={() => onEmitirCFE(f)}
                title="Emitir CFE en Biller"
              >
                {accionando === `${f.id}-emitir` ? '…' : 'Emitir'}
              </button>
            )}
            {puedeEmitirNC && (
              <button
                className="fc-btn-nc"
                onClick={onAbrirNC}
                title="Emitir Nota de Crédito"
              >
                ↩ NC
              </button>
            )}
          </div>
        </td>
      </tr>
      {expandida && (
        <tr className="fc-detalle-row">
          <td colSpan={10}>
            <DetalleFactura f={f} facturas={facturas} />
          </td>
        </tr>
      )}
    </>
  )
}

function DetalleFactura({ f, facturas }) {
  const items = f.biller_payload?.items || []
  const original = f.factura_original_id
    ? facturas.find(x => x.id === f.factura_original_id)
    : null
  const ncRelacionada = !esNotaCredito(f.tipo_cfe)
    ? facturas.find(x => x.factura_original_id === f.id)
    : null

  return (
    <div className="fc-detalle">
      <div className="fc-detalle-grid">
        <div>
          <h4>Receptor</h4>
          <div className="fc-kv"><span>Razón social:</span> {f.receptor_razon_social || 'Consumidor final'}</div>
          <div className="fc-kv"><span>{tipoDocLabel(f.receptor_rut)}:</span> {f.receptor_rut || '—'}</div>
          <div className="fc-kv"><span>Moneda:</span> {f.moneda || 'UYU'}</div>
        </div>
        <div>
          <h4>Importes</h4>
          <div className="fc-kv"><span>Subtotal s/IVA:</span> {fmtMoneda(f.subtotal, f.moneda)}</div>
          <div className="fc-kv"><span>IVA:</span> {fmtMoneda(f.iva_total, f.moneda)}</div>
          <div className="fc-kv"><span>Total c/IVA:</span> <strong>{fmtMoneda(f.monto_total, f.moneda)}</strong></div>
        </div>
        <div>
          <h4>Biller</h4>
          <div className="fc-kv"><span>CFE ID:</span> {f.biller_cfe_id || <em>—</em>}</div>
          <div className="fc-kv"><span>Sincronizado:</span> {f.biller_sync_at ? fmtFecha(f.biller_sync_at) : '—'}</div>
          <div className="fc-kv"><span>Origen:</span> {SYNC_ORIGEN_LABEL[f.sync_origen] || 'Manual'}</div>
        </div>
      </div>

      {items.length > 0 && (
        <>
          <h4>Ítems</h4>
          <table className="fc-items">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cant.</th>
                <th>P.U. s/IVA</th>
                <th>IVA</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                // Biller v2 payload usa snake_case: concepto, precio, indicador_facturacion.
                // Aceptamos también nombres viejos (descripcion, precioUnitario) por si hay facturas legacy.
                const desc  = it.concepto || it.descripcion || '—'
                const cant  = Number(it.cantidad || 0)
                const precio = Number(it.precio ?? it.precioUnitario ?? 0)
                const indIva = it.indicador_facturacion ?? it.indicadorFacturacion
                const ivaLabel = indIva === 3 ? '22%' : indIva === 2 ? '10%' : indIva === 1 ? 'Exento' : '—'
                const codigo = it.codigo ? `[${String(it.codigo).slice(0, 12)}] ` : ''
                return (
                  <tr key={i}>
                    <td>{codigo}{desc}</td>
                    <td className="fc-num">{cant}</td>
                    <td className="fc-num">{fmtMoneda(precio, f.moneda)}</td>
                    <td className="fc-num">{ivaLabel}</td>
                    <td className="fc-num">{fmtMoneda(cant * precio, f.moneda)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}

      {original && (
        <div className="fc-aviso fc-aviso-warn">
          ↩ Esta NC anula{f.nota_credito_monto && Number(f.nota_credito_monto) < Number(original.monto_total) ? ' parcialmente' : ''} la factura{' '}
          <strong>{original.serie ? `${original.serie}-` : ''}{original.numero}</strong> ({fmtFecha(original.fecha_emision)}) · Motivo: <em>{f.nota_credito_motivo || '—'}</em>
        </div>
      )}

      {ncRelacionada && (
        <div className="fc-aviso fc-aviso-warn">
          ↩ Esta factura tiene una NC asociada: <strong>{ncRelacionada.serie ? `${ncRelacionada.serie}-` : ''}{ncRelacionada.numero}</strong> ({fmtFecha(ncRelacionada.fecha_emision)})
        </div>
      )}

      {f.anulada_at && (
        <div className="fc-aviso fc-aviso-error">
          ⚠ Anulada el {fmtFecha(f.anulada_at)} · Motivo: <em>{f.anulada_motivo || '—'}</em>
        </div>
      )}

      {f.biller_response?.errorDGI && (
        <div className="fc-aviso fc-aviso-error">
          ⚠ Error DGI: <code>{f.biller_response.errorDGI}</code>
        </div>
      )}
    </div>
  )
}

function ModalEmitirNC({ factura, onCerrar, onEmitir, accionando }) {
  const [motivo, setMotivo] = useState('')
  const [monto, setMonto] = useState('')
  const [anularTotal, setAnularTotal] = useState(true)

  const total = Number(factura.monto_total || 0)
  const moneda = factura.moneda || 'UYU'

  function submit(e) {
    e.preventDefault()
    if (!motivo.trim()) return
    onEmitir({ motivo, monto, anularTotal })
  }

  return (
    <div className="fc-modal-overlay" onClick={onCerrar}>
      <div className="fc-modal" onClick={e => e.stopPropagation()}>
        <header className="fc-modal-header">
          <h3>↩ Emitir Nota de Crédito</h3>
          <button className="fc-btn-close" onClick={onCerrar}>✕</button>
        </header>
        <div className="fc-modal-body">
          <div className="fc-modal-info">
            <div><strong>Factura original:</strong> {factura.serie ? `${factura.serie}-` : ''}{factura.numero}</div>
            <div><strong>Fecha:</strong> {fmtFecha(factura.fecha_emision)}</div>
            <div><strong>Total:</strong> {fmtMoneda(total, moneda)}</div>
            <div><strong>Receptor:</strong> {factura.receptor_razon_social || 'Consumidor final'}</div>
          </div>

          <form onSubmit={submit}>
            <label className="fc-field">
              <span>Motivo <em>*</em></span>
              <textarea
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                placeholder="Ej: Devolución del pedido. Producto entregado dañado."
                rows={3}
                maxLength={500}
                required
                autoFocus
              />
              <small>{motivo.length}/500 · queda guardado en la NC</small>
            </label>

            <label className="fc-checkbox">
              <input
                type="checkbox"
                checked={anularTotal}
                onChange={e => setAnularTotal(e.target.checked)}
              />
              <span>Anulación total ({fmtMoneda(total, moneda)})</span>
            </label>

            {!anularTotal && (
              <label className="fc-field">
                <span>Monto a acreditar (c/IVA) <em>*</em></span>
                <input
                  type="number"
                  value={monto}
                  onChange={e => setMonto(e.target.value)}
                  min={0}
                  max={total}
                  step={0.01}
                  required
                  placeholder={`Máximo ${total}`}
                />
                <small>Tiene que ser menor o igual al total de la original</small>
              </label>
            )}

            <div className="fc-modal-actions">
              <button type="button" className="fc-btn-secondary" onClick={onCerrar} disabled={accionando}>
                Cancelar
              </button>
              <button type="submit" className="fc-btn-primary" disabled={accionando || !motivo.trim() || (!anularTotal && (!monto || Number(monto) <= 0))}>
                {accionando ? 'Emitiendo…' : '↩ Emitir NC'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Styles
 * ─────────────────────────────────────────────────────────────────────────── */

const css = `
.fc { padding: 20px 24px; max-width: 1400px; margin: 0 auto; font-family: -apple-system, "Inter", system-ui, sans-serif; color: var(--cl-navy, #1A3461); }
.fc-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 18px; flex-wrap: wrap; gap: 12px; }
.fc-header h1 { margin: 0; font-size: 24px; font-weight: 900; }
.fc-sub { margin: 4px 0 0; color: var(--cl-muted, #64748b); font-size: 13px; }

/* KPIs */
.fc-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; margin-bottom: 18px; }
.fc-kpi { background: #fff; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 14px; padding: 16px 18px; }
.fc-kpi-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--cl-muted, #64748b); margin-bottom: 6px; }
.fc-kpi-value { font-size: 26px; font-weight: 900; color: var(--cl-navy, #1A3461); line-height: 1.1; }
.fc-kpi-sub { font-size: 12px; color: var(--cl-muted, #64748b); margin-top: 4px; }
.fc-kpi-teal { border-color: var(--cl-teal, #00A8B5); }
.fc-kpi-teal .fc-kpi-value { color: var(--cl-teal, #00A8B5); }
.fc-kpi-ok { border-color: #16A34A; }
.fc-kpi-ok .fc-kpi-value { color: #16A34A; }
.fc-kpi-warn { border-color: #F59E0B; }
.fc-kpi-warn .fc-kpi-value { color: #B45309; }

/* Mensajes */
.fc-msg { padding: 10px 14px; border-radius: 10px; margin-bottom: 14px; display: flex; justify-content: space-between; gap: 12px; font-size: 13.5px; }
.fc-msg-ok { background: #DCFCE7; color: #15803D; border: 1px solid #86EFAC; }
.fc-msg-error { background: #FEE2E2; color: #B91C1C; border: 1px solid #FCA5A5; }
.fc-msg-warning { background: #FEF3C7; color: #92400E; border: 1px solid #FDE68A; }
.fc-msg-close { background: transparent; border: 0; color: inherit; font-size: 14px; cursor: pointer; opacity: .6; }

/* Filtros */
.fc-filtros { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
.fc-input, .fc-select { padding: 8px 12px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 8px; font-size: 13.5px; font-family: inherit; background: #fff; color: var(--cl-navy, #1A3461); outline: none; }
.fc-input:focus, .fc-select:focus { border-color: var(--cl-teal, #00A8B5); }
.fc-input-search { flex: 1; min-width: 260px; }
.fc-date { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--cl-muted, #64748b); }
.fc-date input { padding: 7px 10px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 8px; font-family: inherit; font-size: 13px; }
.fc-btn-export, .fc-btn-secondary, .fc-btn-sync { padding: 8px 14px; border: 1.5px solid var(--cl-line, #e2e8f0); background: #fff; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; color: var(--cl-navy, #1A3461); font-family: inherit; }
.fc-btn-export:hover, .fc-btn-secondary:hover, .fc-btn-sync:hover { border-color: var(--cl-teal, #00A8B5); color: var(--cl-teal, #00A8B5); }
.fc-btn-export:disabled, .fc-btn-secondary:disabled, .fc-btn-sync:disabled { opacity: .5; cursor: not-allowed; }
.fc-btn-sync { background: var(--cl-teal, #00A8B5); color: #fff; border-color: var(--cl-teal, #00A8B5); }
.fc-btn-sync:hover { background: #008C97; color: #fff; }

/* Tabla */
.fc-tabla-wrap { background: #fff; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 12px; overflow-x: auto; }
.fc-tabla { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.fc-tabla thead { background: #F8FAFC; }
.fc-tabla th { text-align: left; padding: 10px 14px; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: var(--cl-muted, #64748b); border-bottom: 1.5px solid var(--cl-line, #e2e8f0); }
.fc-tabla td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.fc-fila:hover { background: #F8FAFC; }
.fc-fila-anulada { opacity: .55; }
.fc-fila-anulada td { text-decoration: line-through; }
.fc-num { text-align: right; font-variant-numeric: tabular-nums; }
.fc-receptor { max-width: 260px; }
.fc-mini { font-size: 11px; color: var(--cl-muted, #64748b); margin-top: 2px; }
.fc-mini-warn { color: #B45309; font-weight: 700; }
.fc-muted { color: var(--cl-muted, #64748b); font-style: italic; }
.fc-badge { font-size: 11px; font-weight: 800; padding: 3px 9px; border-radius: 999px; white-space: nowrap; }
.fc-acciones { display: flex; gap: 4px; align-items: center; flex-wrap: wrap; }
.fc-btn-link, .fc-btn-toggle { background: transparent; border: 0; padding: 4px 8px; cursor: pointer; font-size: 15px; color: var(--cl-teal, #00A8B5); border-radius: 6px; }
.fc-btn-link:hover, .fc-btn-toggle:hover { background: #ECFEFF; }
.fc-btn-primary-mini { padding: 5px 10px; background: var(--cl-teal, #00A8B5); color: #fff; border: 0; border-radius: 6px; font-size: 11.5px; font-weight: 800; cursor: pointer; font-family: inherit; }
.fc-btn-primary-mini:hover { background: #0891A0; }
.fc-btn-primary-mini:disabled { opacity: .5; cursor: not-allowed; }
.fc-btn-nc { padding: 5px 10px; background: #FEF3C7; color: #92400E; border: 1.5px solid #FDE68A; border-radius: 6px; font-size: 11.5px; font-weight: 800; cursor: pointer; font-family: inherit; }
.fc-btn-nc:hover { background: #FDE68A; }
.fc-footer-tabla { padding: 8px 14px; background: #F8FAFC; font-size: 12px; color: var(--cl-muted, #64748b); border-top: 1px solid var(--cl-line, #e2e8f0); }

/* Detalle */
.fc-detalle-row { background: #F8FAFC; }
.fc-detalle { padding: 14px 18px; }
.fc-detalle-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 18px; margin-bottom: 14px; }
.fc-detalle h4 { margin: 0 0 8px; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: var(--cl-muted, #64748b); }
.fc-kv { font-size: 13px; margin-bottom: 4px; }
.fc-kv span { font-weight: 700; color: var(--cl-muted, #64748b); margin-right: 6px; }
.fc-items { width: 100%; border-collapse: collapse; font-size: 12.5px; margin: 8px 0; }
.fc-items th, .fc-items td { padding: 6px 10px; border-bottom: 1px solid var(--cl-line, #e2e8f0); }
.fc-items th { text-align: left; font-weight: 800; color: var(--cl-muted, #64748b); font-size: 11px; text-transform: uppercase; }
.fc-aviso { padding: 10px 14px; border-radius: 8px; margin-top: 10px; font-size: 13px; }
.fc-aviso-warn { background: #FEF3C7; color: #92400E; border: 1px solid #FDE68A; }
.fc-aviso-error { background: #FEE2E2; color: #B91C1C; border: 1px solid #FCA5A5; }

/* Empty / loading / error */
.fc-empty, .fc-loading { padding: 40px 20px; text-align: center; background: #fff; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 12px; color: var(--cl-muted, #64748b); }
.fc-empty-sub { font-size: 12px; margin-top: 6px; }
.fc-error { padding: 14px; background: #FEE2E2; border: 1px solid #FCA5A5; border-radius: 8px; color: #B91C1C; margin-bottom: 14px; }

/* Modal */
.fc-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, .6); display: flex; align-items: center; justify-content: center; z-index: 999; padding: 16px; }
.fc-modal { background: #fff; border-radius: 16px; max-width: 520px; width: 100%; max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 25px 60px rgba(0,0,0,.3); }
.fc-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--cl-line, #e2e8f0); }
.fc-modal-header h3 { margin: 0; font-size: 17px; font-weight: 900; }
.fc-btn-close { background: transparent; border: 0; font-size: 18px; cursor: pointer; color: var(--cl-muted, #64748b); padding: 4px 8px; border-radius: 6px; }
.fc-btn-close:hover { background: #f1f5f9; }
.fc-modal-body { padding: 18px 20px; overflow-y: auto; }
.fc-modal-info { background: #F8FAFC; border: 1px solid var(--cl-line, #e2e8f0); border-radius: 10px; padding: 12px 14px; margin-bottom: 16px; font-size: 13px; }
.fc-modal-info div { margin-bottom: 4px; }
.fc-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
.fc-field span { font-size: 12px; font-weight: 700; color: var(--cl-navy, #1A3461); }
.fc-field em { color: #ef4444; font-style: normal; margin-left: 2px; }
.fc-field small { font-size: 11px; color: var(--cl-muted, #64748b); margin-top: 2px; }
.fc-field input, .fc-field textarea { padding: 9px 12px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 8px; font-size: 13.5px; font-family: inherit; color: var(--cl-navy, #1A3461); outline: none; }
.fc-field input:focus, .fc-field textarea:focus { border-color: var(--cl-teal, #00A8B5); }
.fc-field textarea { resize: vertical; }
.fc-checkbox { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; cursor: pointer; font-size: 13.5px; }
.fc-checkbox input { width: 16px; height: 16px; cursor: pointer; }
.fc-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--cl-line, #e2e8f0); }
.fc-btn-primary { padding: 9px 18px; background: var(--cl-teal, #00A8B5); color: #fff; border: 0; border-radius: 8px; font-size: 13.5px; font-weight: 800; cursor: pointer; font-family: inherit; }
.fc-btn-primary:hover { background: #0891A0; }
.fc-btn-primary:disabled { opacity: .5; cursor: not-allowed; }

@media (max-width: 720px) {
  .fc { padding: 14px; }
  .fc-tabla th:nth-child(8), .fc-tabla td:nth-child(8) { display: none; }
  .fc-tabla th:nth-child(4), .fc-tabla td:nth-child(4) { display: none; }
}
`
